import type { NaicsCodeId } from "@inrev/common";
import { NAICSCodesList } from "@portal/views/admin/shared/NAICSCodeList";
import { useState } from "react";
import { NAICSCodeSelectInput } from "../../form/inputs/NAICSCodeSelectInput";
import { Card, ClickableIcon, Text } from "../../primitives";

export type AccountSpecialtyFieldProps = {
	value: NaicsCodeId[];
	onChange: (ids: NaicsCodeId[]) => void;
	/** Fires when the picker modal closes — wire to persist the draft. */
	onBlur?: () => void;
	/** Controlled picker-open state. Falls back to internal state when omitted. */
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	readOnly?: boolean;
	testId?: string;
};

/**
 * Account specialty (NAICS) field. Raised card with the pencil in the title row. The pencil
 * opens `NAICSCodeSelectInput`'s picker (driven headless via controlled open).
 */
export const AccountSpecialtyField = ({
	value,
	onChange,
	onBlur,
	open,
	onOpenChange,
	readOnly = false,
	testId = "account-specialty",
}: AccountSpecialtyFieldProps) => {
	const [internalOpen, setInternalOpen] = useState(false);
	const isControlled = open !== undefined;
	const pickerOpen = isControlled ? open : internalOpen;
	const setPickerOpen = (next: boolean) => {
		if (isControlled) onOpenChange?.(next);
		else setInternalOpen(next);
	};

	return (
		<Card elevation="raised" className="rounded-xl" data-testid={testId}>
			<Card.Body className="flex w-full flex-col p-6 space-y-5">
				<div className="flex items-center justify-between w-full">
					<Text type="title" color="primary" className="flex-1">
						Specialty
					</Text>
					{!readOnly && (
						<ClickableIcon
							data-testid={`${testId}-edit`}
							type="pencil-square"
							size="mini"
							color="secondary"
							onClick={() => setPickerOpen(true)}
						/>
					)}
				</div>
				<NAICSCodesList
					naicsCodes={value}
					showRatio
					emptyMessage="None"
					className="w-full min-w-0 max-w-none border-0 bg-transparent"
					panelClassName="max-h-[250px] overflow-y-auto overscroll-y-contain"
				/>
			</Card.Body>
			{!readOnly && (
				<NAICSCodeSelectInput
					open={pickerOpen}
					onOpenChange={setPickerOpen}
					value={value}
					onChange={onChange}
					onBlur={onBlur}
					title="Select Account Specialties"
					selectText="Select specialties"
					editText="Edit Specialties"
					searchPlaceholder="Search specialties..."
				/>
			)}
		</Card>
	);
};
